import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarClock, Loader2 } from 'lucide-react';
import { ApiError, visits, type VisitRow } from '@/api/client';
import {
  fmtDate,
  fmtPlainDay,
  fromDateTimeInput,
  getDisplayTimeZone,
  toDateTimeInput,
  visitWindowBounds,
} from '@/lib/dates';

/**
 * Move a subject's appointment, with a time of day.
 *
 * The picker is bounded by the visit's protocol window — planned date minus
 * `window_minus_days` through plus `window_plus_days` — so a visit nominally
 * on 15 Oct with ±3 days offers 12–18 Oct and nothing else. The server
 * re-checks the same bounds; `min`/`max` here is courtesy, not security.
 *
 * Only `scheduled_at` moves. `planned_date` is the window's anchor and stays
 * where the protocol put it, which is what stops a visit being walked out of
 * its own window three days at a time.
 */
export function RescheduleVisitDialog({
  visit,
  open,
  onClose,
}: {
  visit: VisitRow;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const bounds = visitWindowBounds(visit);
  const [when, setWhen] = useState(() =>
    toDateTimeInput(visit.scheduled_at ?? visit.planned_date ?? new Date().toISOString()),
  );
  const [reason, setReason] = useState('');

  const mut = useMutation({
    mutationFn: async () => {
      // fromDateTimeInput returns '' for a half-typed value rather than
      // inventing a date — so an empty result means "not ready", not an error
      // worth an ApiError.
      const iso = fromDateTimeInput(when);
      if (!iso) throw new Error('Pick a date and time.');
      // `when` is already on the site's clock, so its date half IS the day
      // the user picked — no conversion, nothing to get wrong.
      return visits.reschedule(visit.visit_id, {
        scheduled_at: iso,
        scheduled_day: when.slice(0, 10),
        reason: reason.trim() || undefined,
      });
    },
    onSuccess: () => {
      toast.success('Visit rescheduled');
      // Every surface that lists visits must re-read. These keys are not
      // guessable — 'visits-cal' is the calendar's own, and it is NOT a
      // prefix of 'visits', so invalidating 'visits' alone leaves the page
      // the user is looking at showing the old slot.
      void qc.invalidateQueries({ queryKey: ['visits-cal'] });
      void qc.invalidateQueries({ queryKey: ['visits'] });
      void qc.invalidateQueries({ queryKey: ['visit', visit.visit_id] });
      void qc.invalidateQueries({ queryKey: ['dashboard', 'visits'] });
      onClose();
    },
    onError: (e: unknown) =>
      toast.error(
        e instanceof ApiError || e instanceof Error
          ? e.message
          : 'Could not reschedule this visit.',
      ),
  });

  if (!open) return null;

  const recorded = !!(visit.actual_date ?? visit.completed_at);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mut.mutate();
        }}
        className="card w-full max-w-md space-y-4 p-6"
      >
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-ink-muted" />
          <h3 className="text-lg font-semibold">Reschedule visit</h3>
        </div>

        {recorded ? (
          <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            This visit has already been recorded, so it can no longer be rescheduled. Change the
            actual visit date instead.
          </p>
        ) : (
          <>
            <div>
              <label className="label text-xs">New date and time *</label>
              <input
                type="datetime-local"
                className="input"
                required
                value={when}
                min={bounds ? `${bounds.min}T00:00` : undefined}
                max={bounds ? `${bounds.max}T23:59` : undefined}
                onChange={(e) => setWhen(e.target.value)}
              />
              <p className="mt-1 text-[11px] text-ink-muted">
                {bounds ? (
                  <>
                    {/* fmtPlainDay, not fmtDate: a bound is a calendar day, and
                        resolving it as a UTC-midnight instant in the site's zone
                        showed it a day early for anywhere behind UTC. */}
                    Protocol window: {fmtPlainDay(bounds.min)} – {fmtPlainDay(bounds.max)}
                    {visit.planned_date && (
                      <> · planned {fmtDate(visit.planned_date)}</>
                    )}
                  </>
                ) : (
                  'This visit has no protocol window.'
                )}
              </p>
              <p className="mt-0.5 text-[11px] text-ink-muted">
                Times are in this site's timezone ({getDisplayTimeZone()}).
              </p>
            </div>

            <div>
              <label className="label text-xs">Reason (optional)</label>
              <input
                className="input"
                maxLength={2000}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. subject requested a later slot"
              />
            </div>
          </>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            {recorded ? 'Close' : 'Cancel'}
          </button>
          {!recorded && (
            <button type="submit" className="btn-primary" disabled={mut.isPending || !when}>
              {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
              Reschedule
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
